import axios from "axios";
import React, { useState, useMemo, useRef, useEffect } from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import {
  UserData,
  UserStatus,
  UserAreaOfResponsibilityResponse,
  UserRole
} from "@/entities/users/types";
import { APILookupValue } from "@/services/api/types";
import {
  useUsersAll,
  useRoles,
  useCreateUserMutation,
  useAreaOfResponsibility,
  useAreaOfResponsibilityUpsert
} from "@/entities/users/hooks";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useTranslation } from "react-i18next";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import UserAvatar from "@/components/controls/UserAvatar";
import CreateNewOptionModal from "./CreateNewOptionModal";

const TRANSLATION_KEY = "settings.userManagement.addNewUserModal";

interface AddNewUserModalProps {
  isOpen: boolean;
  onClose: (userWasAdded?: boolean) => void;
}

interface InvitationSentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  onAddAnother: () => void;
}

const InvitationSentModal: React.FC<InvitationSentModalProps> = ({
  isOpen,
  onClose,
  userName,
  onAddAnother
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      title={
        <Text variant="title3H1">
          {t(`${TRANSLATION_KEY}.invitationSentModal.title`)}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 p-6">
          <Text variant="body">
            {t(`${TRANSLATION_KEY}.invitationSentModal.description`, {
              userName
            })}
          </Text>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondaryNoBg" onClick={onAddAnother}>
            {t(`${TRANSLATION_KEY}.invitationSentModal.addAnotherUser`)}
          </Button>
          <Button onClick={onClose}>
            {t(`${TRANSLATION_KEY}.invitationSentModal.gotIt`)}
          </Button>
        </div>
      }
      contentClassname="max-w-md"
      hideDivider={true}
    />
  );
};

const AddNewUserModal: React.FC<AddNewUserModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const { users } = useUsersAll();
  const { data: rolesData } = useRoles();
  const createUserMutation = useCreateUserMutation();
  const formRef = useRef<HTMLFormElement>(null);
  const {
    data: areaOfResponsibility,
    refetch: refetchAreaOfResponsibility
  }: {
    data: UserAreaOfResponsibilityResponse | undefined;
    refetch: () => void;
  } = useAreaOfResponsibility();

  const areaOfResponsibilityUpsert = useAreaOfResponsibilityUpsert(
    (response: APILookupValue) => {
      setAreaOptions((prev: APILookupValue[]) => [...prev, response]);
      setFilteredAreaOptions((prev: APILookupValue[]) => [...prev, response]);
      refetchAreaOfResponsibility();

      if (formSetValue) {
        formSetValue("areaOfResponsibility", response.id);
      }
    }
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [areaOptions, setAreaOptions] = useState<APILookupValue[]>([]);
  const [filteredAreaOptions, setFilteredAreaOptions] = useState<
    APILookupValue[]
  >([]);
  const [areaSearchTerm, setAreaSearchTerm] = useState("");
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [submittedUserName, setSubmittedUserName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [shouldDisable, setShouldDisable] = useState(false);
  const [formSetValue, setFormSetValue] = useState<
    ((field: string, value: unknown) => void) | null
  >(null);

  // Watch for email field changes and clear error when user starts typing
  const [watchAllFields, setWatchAllFields] = useState<Record<string, unknown>>(
    {}
  );

  const erroredEmailRef = useRef<string | null>(null);

  useEffect(() => {
    // Clear only on a genuinely new email value — guards against watch() churn
    // wiping the error mid-display.
    const currentEmail = watchAllFields.email;
    if (
      shouldDisable &&
      typeof currentEmail === "string" &&
      currentEmail !== "" &&
      currentEmail !== erroredEmailRef.current
    ) {
      setShouldDisable(false);
      setErrorMessage("");
      erroredEmailRef.current = null;
    }
  }, [watchAllFields.email, shouldDisable]);

  useEffect(() => {
    const areas = areaOfResponsibility?.area_of_responsibility;

    if (areas && Array.isArray(areas)) {
      setAreaOptions(areas);
      setFilteredAreaOptions(areas);
    } else {
      setAreaOptions([]);
      setFilteredAreaOptions([]);
    }
  }, [areaOfResponsibility]);

  const roleOptions = useMemo(() => {
    if (!rolesData?.roles) return [];
    return rolesData.roles.map((role: UserRole) => ({
      id: role.id,
      displayName: role.name,
      subtitle: role.description
    }));
  }, [rolesData]);

  const { options: availableManagers } = useEntitySearch({
    entityType: EntityType.USER,
    filters: {
      status: [UserStatus.ACTIVE]
    }
  });

  const handleCreateNewOption = async (optionName: string) => {
    try {
      await areaOfResponsibilityUpsert.mutateAsync({
        name: optionName
      });
    } catch (error) {
      console.error("Failed to create area of responsibility:", error);
    }
  };

  const handleAreaSearch = (searchTerm: string) => {
    setAreaSearchTerm(searchTerm);
    if (searchTerm.trim() === "") {
      setFilteredAreaOptions(areaOptions);
      return;
    }

    const filtered = areaOptions.filter((option: APILookupValue) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAreaOptions(filtered);
  };

  const shouldShowCreateNew =
    filteredAreaOptions.length === 0 || areaOptions.length === 0;

  const checkEmailExists = (email: string): boolean => {
    if (!users || !email) return false;
    return users.some(
      (user) => user.email && user.email.toLowerCase() === email.toLowerCase()
    );
  };

  const validateEmail = (email: string): string => {
    if (!email) return "";

    if (checkEmailExists(email)) {
      return t(`${TRANSLATION_KEY}.emailAlreadyExists`);
    }

    return "";
  };

  const handleSubmit = async (data: Partial<UserData>) => {
    const emailValidationError = validateEmail(data.email || "");
    if (emailValidationError) {
      erroredEmailRef.current = data.email ?? null;
      setShouldDisable(true);
      setErrorMessage(emailValidationError);
      return;
    }
    setShouldDisable(false);
    setErrorMessage("");

    // Get department ID from selected role
    const selectedRole = rolesData?.roles?.find(
      (role: UserRole) => role.id === data.role
    );
    const departmentId = selectedRole?.department || undefined;

    const userData: Partial<UserData> = {
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: `${data.firstName} ${data.lastName}`,
      email: data.email,
      department: departmentId || 0,
      lastLoginAt: "",
      timezone: "",
      role: data.role,
      jobTitle: data.title || "",
      avatarUrl: "",
      groupIds: [],
      managerId: data.reportsTo || null,
      status: undefined,
      ...(data.areaOfResponsibility && {
        areaOfResponsibility: data.areaOfResponsibility
      })
    };

    try {
      await createUserMutation.mutateAsync(userData as UserData);
      setSubmittedUserName(`${userData.firstName} ${userData.lastName}`);
      setShowInvitationModal(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        erroredEmailRef.current = data.email ?? null;
        setShouldDisable(true);
        setErrorMessage(t(`${TRANSLATION_KEY}.emailAlreadyExists`));
      } else {
        setErrorMessage(t(`${TRANSLATION_KEY}.createUserError`));
      }
    }
  };

  const handleAddAnother = () => {
    setShowInvitationModal(false);
    formRef.current?.reset();
    setIsFormValid(false);
    setShouldDisable(false);
    setErrorMessage("");
  };

  const handleCancel = () => {
    onClose(false);
    formRef.current?.reset();
    setIsFormValid(false);
    setShouldDisable(false);
    setErrorMessage("");
  };

  const handleInvitationModalClose = () => {
    setShowInvitationModal(false);
    onClose(true);
  };

  const sections: FormSectionProps[] = [
    {
      id: "userDetails",
      fields: [
        {
          name: "firstName",
          label: t(`${TRANSLATION_KEY}.fields.name`),
          type: "text",
          required: true,
          labelPosition: "left",
          layout: "half",
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.firstName`)
          }
        },
        {
          name: "lastName",
          label: "",
          type: "text",
          required: true,
          labelPosition: "left",
          layout: "half",
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.lastName`)
          }
        },
        {
          name: "email",
          label: t(`${TRANSLATION_KEY}.fields.email`),
          type: "email",
          required: true,
          labelPosition: "left",
          layout: "flex",
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.email`)
          }
        },
        {
          name: "title",
          label: t(`${TRANSLATION_KEY}.fields.title`),
          type: "text",
          required: false,
          labelPosition: "left",
          layout: "flex",
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.title`)
          }
        },
        {
          name: "role",
          label: t(`${TRANSLATION_KEY}.fields.role`),
          type: "singleSelect",
          required: true,
          labelPosition: "left",
          layout: "flex",
          fieldActionProps: {
            options: roleOptions,
            placeholder: t(`${TRANSLATION_KEY}.placeholders.role`),
            showSearch: true,
            variant: "form",
            showSelectedItemSubtitle: false,
            dropdownDirection: "bottom"
          }
        },
        {
          name: "reportsTo",
          label: t(`${TRANSLATION_KEY}.fields.reportsTo`),
          type: "singleSelect",
          required: false,
          labelPosition: "left",
          layout: "flex",
          fieldActionProps: {
            options: availableManagers,
            placeholder: t(`${TRANSLATION_KEY}.placeholders.manager`),
            showSearch: true,
            variant: "form",
            dropdownDirection: "bottom",
            minItemHeight: "46px",
            renderIcon: (userId: string | number) => {
              const option = availableManagers.find((opt) => opt.id === userId);
              const user = option?.userData as UserData;
              return user ? (
                <UserAvatar userData={user} size="M" />
              ) : (
                <div className="size-5" />
              );
            }
          }
        },
        {
          name: "areaOfResponsibility",
          label: t(`${TRANSLATION_KEY}.fields.areaOfResponsibility`),
          type: "singleSelect",
          required: false,
          labelPosition: "left",
          layout: "flex",
          fieldActionProps: {
            options: filteredAreaOptions,
            placeholder: t(
              `${TRANSLATION_KEY}.placeholders.areaOfResponsibility`
            ),
            showSearch: true,
            variant: "form",
            onSearch: handleAreaSearch,
            initialSearchTerm: areaSearchTerm,
            dropdownDirection: "bottom",
            popOverFooter: shouldShowCreateNew ? (
              <AddButtonFooter
                buttonText={t(`${TRANSLATION_KEY}.createNewOption`)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setNewOptionName(areaSearchTerm || "");
                  setShowCreateOptionModal(true);
                }}
                variant="iconNoBg"
                disabled={!areaSearchTerm.trim()}
              />
            ) : undefined
          }
        }
      ]
    }
  ];

  return (
    <>
      <Modal
        open={isOpen && !showInvitationModal}
        setIsOpen={(open) => !open && handleCancel()}
        title={<Text variant="title3H1">{t(`${TRANSLATION_KEY}.title`)}</Text>}
        content={
          <div className="flex flex-col gap-3 w-[35rem] p-4 overflow-y-auto">
            <FormNew
              sections={sections}
              // Keep the user's input on a failed submit (e.g. duplicate email) so
              // they can fix it; the modal resets itself on success/cancel/add-another.
              shouldNotResetOnSubmit
              isFormValid={(isValid, watchAllFields) => {
                setIsFormValid(isValid);
                setWatchAllFields(watchAllFields || {});
              }}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={(formMethods) => {
                setFormSetValue(() => formMethods.setValue);
              }}
            />
            {errorMessage && (
              <Text variant="smBodyError" className="mt-2 px-4">
                {errorMessage}
              </Text>
            )}
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button variant="secondaryNoBg" onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={!isFormValid || shouldDisable}
            >
              {t(`${TRANSLATION_KEY}.addUser`)}
            </Button>
          </div>
        }
        contentClassname="max-w-2xl"
        hideDivider={true}
      />

      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        optionName={newOptionName}
      />

      <InvitationSentModal
        isOpen={showInvitationModal}
        onClose={handleInvitationModalClose}
        userName={submittedUserName}
        onAddAnother={handleAddAnother}
      />
    </>
  );
};

export default AddNewUserModal;
